'use client';

/**
 * Application Providers - Root provider composition
 *
 * Auth gate: app shows login screen until a user is selected and logged in.
 * In dev: dev-user-store loads personas from dev_tokens.json.
 * In prod: replace with Google OAuth / external IdP.
 *
 * @layer infrastructure
 * @arch_component_type provider
 * @arch_domain app_shell
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import { useState, useEffect } from 'react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useCartStore } from '@/lib/stores/cart-store';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useDevUserStore, type DevUser } from '@/lib/stores/dev-user-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { api } from '@/lib/api/client';
import { MagicLinkForm } from '@/components/auth/magic-link-form';
import { PasskeySignIn } from '@/components/auth/passkey-sign-in';
import { TokenFileForm } from '@/components/auth/token-file-form';

function LoginScreen() {
  const { users, isLoaded, selectUser } = useDevUserStore();

  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center bg-background text-foreground">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const hasPersonas = users.length > 0;

  return (
    <div className="h-screen flex items-center justify-center bg-background text-foreground">
      <div className="w-80 space-y-4">
        <div className="text-center space-y-1">
          <h1 className="text-lg font-semibold">Klyde</h1>
          <p className="text-xs text-muted-foreground">Sign in to continue</p>
        </div>

        <PasskeySignIn />

        <MagicLinkForm />

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="flex-1 h-px bg-border" />
          <span>or</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <TokenFileForm />

        {hasPersonas && (
          <>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border" />
              <span>or pick a dev persona</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div className="space-y-1">
              {users.map((user) => (
                <button
                  key={user.name}
                  onClick={() => selectUser(user)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-border hover:bg-accent/50 text-left transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                    {user.name.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const isLoaded = useDevUserStore((s) => s.isLoaded);

  // Rehydrate persisted stores on mount
  useEffect(() => {
    useLayoutStore.persist.rehydrate();
    useCartStore.persist.rehydrate();
    // Load dev users (triggers login for persisted user if any)
    useDevUserStore.getState().loadUsers();
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  // On login: fetch role-filtered matrix, invalidate stale queries, reset mode
  useEffect(() => {
    api.onLogin(async (response) => {
      const lang = useLanguageStore.getState().lang ?? undefined;
      await useTotalMatrixStore.getState().fetchTotalMatrix(response.role, lang);
      // Load available capabilities from graph (filtered by JWT scopes)
      const { loadCapabilities } = await import('@/lib/stores/capability-store').then(m => m.useCapabilityStore.getState());
      loadCapabilities();  // fire-and-forget — non-blocking
      queryClient.invalidateQueries();
      // Clear stale selection state from previous user session
      const { useGraphSelectionStore } = await import('@/lib/stores/graph-selection-store');
      useGraphSelectionStore.getState().clearSelection();
      // Reset mode to user's default (first nav item) — prevents seller on buyer-only view
      const nav = useTotalMatrixStore.getState().nav;
      if (nav.length > 0) {
        const defaultMode = nav.find(n => n.mode !== 'home')?.mode || nav[0].mode;
        useLayoutStore.getState().setActiveExplorerView(defaultMode);
      }
    });
  }, [queryClient]);

  // On language toggle: refetch total matrix so schema-driven explorers
  // get fresh explorer_labels for the new language. No-op when not logged in.
  const currentLang = useLanguageStore((s) => s.lang);
  useEffect(() => {
    if (!selectedUser) return;
    const activeRole = useTotalMatrixStore.getState().activeRole ?? undefined;
    // fetchTotalMatrix short-circuits if (role, lang) matches the active pair,
    // so this is safe to call on every render.
    useTotalMatrixStore.getState().fetchTotalMatrix(activeRole, currentLang ?? undefined);
  }, [currentLang, selectedUser]);

  const isAuthenticated = isLoaded && selectedUser;

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <QueryClientProvider client={queryClient}>
        {isAuthenticated ? children : <LoginScreen />}
        <Toaster position="top-right" richColors />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
